import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { TopBar } from "@/components/layout/TopBar";
import { Sidebar } from "@/components/layout/Sidebar";

interface SystemShellProps {
  children: ReactNode;
}

/**
 * Shell rendered at /system/*. Visually distinct from the tenant AppShell
 * (deeper bg, "SYSTEM" wordmark) so the platform owner can tell at a
 * glance whether they're acting on their own console or inside a
 * customer tenant via view-as.
 */
export function SystemShell({ children }: SystemShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-dw-bg-deep">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-dw-bg focus:text-dw-text focus:border focus:border-dw-border focus:rounded-dw-sm focus:px-dw-3 focus:py-dw-2 focus:shadow-lg"
      >
        Skip to main content
      </a>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          "fixed z-40 top-0 left-0 h-full transition-transform md:relative md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          sidebarCollapsed ? "w-16" : "w-60",
        ].join(" ")}
      >
        <Sidebar
          audience="platform"
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        />
      </aside>

      <div className="flex flex-1 flex-col min-w-0">
        <TopBar
          onMenuClick={() => setMobileOpen((o) => !o)}
          brand={<SystemBrand />}
        />

        <main id="main-content" className="flex-1 overflow-y-auto" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

function SystemBrand() {
  return (
    <Link
      to="/system/overview"
      aria-label="Dorsey-Worx System Console — go to overview"
      className="font-display text-lg tracking-wider uppercase whitespace-nowrap select-none hover:opacity-80 transition-opacity"
    >
      <span className="text-dw-text">DORSEY</span>
      <span className="text-dw-blue mx-1">|</span>
      <span className="text-dw-text">WORX</span>
      <span className="text-dw-gold ml-2 text-xs font-medium tracking-widest">
        SYSTEM
      </span>
    </Link>
  );
}
